/* Hallmark · pre-emit critique: P4 H4 E4 S4 R5 V4. Existing Mantine and cobalt tokens. */
import { Alert, Button, Container, Divider, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, get, post, type Me } from './api';
import { DeviceKeys } from './DeviceKeys';
import { DeviceApproval, pendingDeviceCode } from './DeviceApproval';

type Release = { version: string; minAndroid: number; url: string };

async function release(): Promise<Release> {
  const data = await get<Release>('/downloads/android-core/latest.json');
  if (!/^\d+\.\d+\.\d+$/.test(data.version) ||
      !/^\/downloads\/android-core\/feda-core-\d+\.\d+\.\d+\.apk$/.test(data.url) ||
      !Number.isInteger(data.minAndroid) || data.minAndroid < 1) {
    throw new Error('Не удалось проверить данные выпуска.');
  }
  return data;
}
async function testingRelease(): Promise<Release> {
  const data = await get<Release>('/downloads/android-core/testing-installer.json');
  if (!/^\d+\.\d+\.\d+-test\.\d+$/.test(data.version) ||
      data.url !== `/downloads/android-core/feda-core-${data.version}-installer.apk` ||
      !Number.isInteger(data.minAndroid) || data.minAndroid < 1) throw new Error('Недействительный тестовый выпуск.');
  return data;
}

export function App() {
  const current = useQuery({ queryKey: ['release'], queryFn: release, retry: false });
  const testing = useQuery({ queryKey: ['testing-installer'], queryFn: testingRelease, retry: false });
  const [deviceCode, setDeviceCode] = useState(pendingDeviceCode);
  const session = useQuery({ queryKey: ['me'], queryFn: () => get<Me>('/api/me'), retry: false });
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const signOut = async () => {
    setLoggingOut(true);
    setLogoutError('');
    try {
      const { centralLogoutUrl } = await post<{ centralLogoutUrl: string }>('/api/logout');
      const form = document.createElement('form');
      form.method = 'post';
      form.action = centralLogoutUrl;
      form.hidden = true;
      document.body.appendChild(form);
      form.submit();
    } catch {
      setLoggingOut(false);
      setLogoutError('Не удалось выйти. Повторите при работающей связи.');
    }
  };
  const signedOut = session.error instanceof ApiError && session.error.status === 401;

  return <Container component="main" size="sm" py="xl">
    <Stack gap="xl">
      <div>
        <Title order={1} className="page-title">FEDA VPN</Title>
        <Text c="dimmed" mt="sm">Приложение для Android</Text>
      </div>
      {new URLSearchParams(window.location.search).get('registered') === '1' && session.data?.vpnUser?.status === 'active' &&
        <Alert color="green" title="Аккаунт готов">Доступ к VPN активирован. Установите приложение и войдите тем же аккаунтом.</Alert>}
      {deviceCode && <DeviceApproval code={deviceCode} signedIn={!!session.data} onClose={() => setDeviceCode('')} />}
      <Stack gap="sm">
        <Title order={2}>Скачать приложение</Title>
        {current.isPending ? <Loader size="sm" aria-label="Проверяем выпуск" /> : current.data ? <>
          <Text>Версия {current.data.version} · Android {current.data.minAndroid}+</Text>
          <Button component="a" href={current.data.url} download fullWidth>Скачать APK</Button>
          <Text size="sm" c="dimmed">Откройте скачанный файл на телефоне. Для установки Android запросит разрешение.</Text>
        </> : <Alert color="red" title="Скачивание временно недоступно">
          <Stack gap="sm"><Text size="sm">Не удалось получить данные выпуска.</Text>
            <Button variant="light" loading={current.isFetching} onClick={() => void current.refetch()}>Повторить</Button>
          </Stack>
        </Alert>}
      </Stack>
      {testing.data && <Stack gap="sm">
        <Button component="a" href={testing.data.url} download variant="outline" fullWidth>Скачать тестовую версию</Button>
        <Text size="sm" c="dimmed">{testing.data.version} · Android {testing.data.minAndroid}+ · для проверки новых возможностей. Устанавливается поверх текущего приложения.</Text>
      </Stack>}
      <Divider />
      {session.data?.vpnUser?.status === 'active' && <>
        <DeviceKeys key={session.data.user.id} accountId={session.data.user.id} />
        <Divider />
      </>}
      <Stack gap="sm">
        <Title order={2}>Аккаунт</Title>
        {session.isPending ? <Loader size="sm" aria-label="Проверяем вход" /> : session.data ? <>
          <Text>Вы вошли{session.data.user.displayName || session.data.user.name ? `: ${session.data.user.displayName || session.data.user.name}` : ''}.</Text>
          <Text size="sm" c="dimmed">Подключение и настройки доступны в Android-приложении.</Text>
          <Group><Button variant="light" loading={loggingOut} onClick={() => void signOut()}>Выйти</Button></Group>
        </> : signedOut ? <>
          <Text c="dimmed">Войдите через системный браузер. На телефоне вход также доступен из приложения.</Text>
          <Button component="a" href="/auth/login" variant="light" fullWidth>Войти</Button>
        </> : <Alert color="red" title="Не удалось проверить вход">
          <Stack gap="sm"><Text size="sm">Скачивание приложения доступно независимо от проверки аккаунта.</Text>
            <Button variant="light" loading={session.isFetching} onClick={() => void session.refetch()}>Повторить вход</Button>
          </Stack>
        </Alert>}
        {logoutError && <Alert color="red">{logoutError}</Alert>}
      </Stack>
    </Stack>
  </Container>;
}
